import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import {
    Bold as BoldIcon,
    Italic as ItalicIcon,
    Underline as UnderlineIcon,
    Strikethrough,
    List,
    ListOrdered,
    Heading1,
    Heading2,
    Heading3,
    Quote,
    Undo2,
    Redo2,
    Link as LinkIcon,
    Eraser,
} from 'lucide-react';

/**
 * Word-style rich text editor (TipTap) for contract terms and similar
 * long-form HTML content. Stores HTML; `value` and `onChange` use HTML strings.
 *
 * Toolbar: bold / italic / underline / strike / H1-H3 / lists / quote / link / clear.
 */
export default function RichTextEditor({
    value,
    onChange,
    placeholder,
    minHeight = 280,
    testId = 'rich-text-editor',
}) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
            }),
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            // TipTap emits "<p></p>" for empty — normalise to empty string.
            onChange?.(html === '<p></p>' ? '' : html);
        },
        editorProps: {
            attributes: {
                class: 'rte-content',
                'data-testid': `${testId}-content`,
                spellcheck: 'true',
            },
        },
    });

    // Keep external value in sync when it changes from outside (e.g. "Load default" button)
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const next = value || '';
        // Compare without DOM noise — string-equal is enough for our usage
        if ((next || '<p></p>') !== current) {
            editor.commands.setContent(next || '', { emitUpdate: false });
        }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [value, editor]);

    if (!editor) {
        return <div className="rte-shell rte-loading" style={{ minHeight }}>Loading editor…</div>;
    }

    const isActive = (name, attrs) => editor.isActive(name, attrs);

    const setLink = () => {
        const previous = editor.getAttributes('link').href;
        const url = window.prompt('Link URL', previous || 'https://');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const clearFormatting = () => {
        editor.chain().focus().clearNodes().unsetAllMarks().run();
    };

    return (
        <div className="rte-shell" data-testid={testId}>
            <RteStyles />
            <div className="rte-toolbar" role="toolbar" aria-label="Formatting">
                <RteBtn
                    label="Bold"
                    active={isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    testId={`${testId}-bold`}
                >
                    <BoldIcon size={14} />
                </RteBtn>
                <RteBtn
                    label="Italic"
                    active={isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    testId={`${testId}-italic`}
                >
                    <ItalicIcon size={14} />
                </RteBtn>
                <RteBtn
                    label="Underline"
                    active={isActive('underline')}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    testId={`${testId}-underline`}
                >
                    <UnderlineIcon size={14} />
                </RteBtn>
                <RteBtn
                    label="Strikethrough"
                    active={isActive('strike')}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    testId={`${testId}-strike`}
                >
                    <Strikethrough size={14} />
                </RteBtn>
                <RteDivider />
                <RteBtn
                    label="Heading 1"
                    active={isActive('heading', { level: 1 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    testId={`${testId}-h1`}
                >
                    <Heading1 size={14} />
                </RteBtn>
                <RteBtn
                    label="Heading 2"
                    active={isActive('heading', { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    testId={`${testId}-h2`}
                >
                    <Heading2 size={14} />
                </RteBtn>
                <RteBtn
                    label="Heading 3"
                    active={isActive('heading', { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    testId={`${testId}-h3`}
                >
                    <Heading3 size={14} />
                </RteBtn>
                <RteDivider />
                <RteBtn
                    label="Bullet list"
                    active={isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    testId={`${testId}-bullet`}
                >
                    <List size={14} />
                </RteBtn>
                <RteBtn
                    label="Numbered list"
                    active={isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    testId={`${testId}-ordered`}
                >
                    <ListOrdered size={14} />
                </RteBtn>
                <RteBtn
                    label="Quote"
                    active={isActive('blockquote')}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    testId={`${testId}-quote`}
                >
                    <Quote size={14} />
                </RteBtn>
                <RteDivider />
                <RteBtn
                    label="Link"
                    active={isActive('link')}
                    onClick={setLink}
                    testId={`${testId}-link`}
                >
                    <LinkIcon size={14} />
                </RteBtn>
                <RteBtn
                    label="Clear formatting"
                    onClick={clearFormatting}
                    testId={`${testId}-clear`}
                >
                    <Eraser size={14} />
                </RteBtn>
                <RteDivider />
                <RteBtn
                    label="Undo"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    testId={`${testId}-undo`}
                >
                    <Undo2 size={14} />
                </RteBtn>
                <RteBtn
                    label="Redo"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    testId={`${testId}-redo`}
                >
                    <Redo2 size={14} />
                </RteBtn>
            </div>
            <div className="rte-area" style={{ minHeight }}>
                {(!value || value === '<p></p>') && placeholder && (
                    <div className="rte-placeholder">{placeholder}</div>
                )}
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}

function RteBtn({ label, active, onClick, disabled, children, testId }) {
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={!!active}
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className={`rte-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
        >
            {children}
        </button>
    );
}

function RteDivider() {
    return <span className="rte-divider" aria-hidden="true" />;
}

function RteStyles() {
    return (
        <style>{`
        .rte-shell {
            border: 1px solid var(--bm-border, #e2e8f0);
            border-radius: 10px;
            background: #fff;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        }
        .rte-loading {
            padding: 16px;
            font-size: 13px;
            color: var(--bm-text-muted, #64748b);
        }
        .rte-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 2px;
            padding: 6px 8px;
            border-bottom: 1px solid var(--bm-border, #e2e8f0);
            background: var(--bm-bg-mute, #f8fafc);
            position: sticky;
            top: 0;
            z-index: 1;
        }
        .rte-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border-radius: 6px;
            border: 1px solid transparent;
            background: transparent;
            color: var(--bm-text, #0f172a);
            cursor: pointer;
            transition: background .12s, border-color .12s, color .12s;
        }
        .rte-btn:hover:not(.is-disabled) {
            background: #fff;
            border-color: var(--bm-border, #e2e8f0);
        }
        .rte-btn.is-active {
            background: var(--bm-accent-50, #ecfdf5);
            border-color: var(--bm-primary, #0B3C5D);
            color: var(--bm-primary, #0B3C5D);
        }
        .rte-btn.is-disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
        .rte-divider {
            width: 1px;
            height: 18px;
            background: var(--bm-border, #e2e8f0);
            margin: 0 4px;
        }
        .rte-area {
            position: relative;
            padding: 14px 16px;
        }
        .rte-placeholder {
            position: absolute;
            top: 14px;
            left: 16px;
            color: var(--bm-text-subtle, #94a3b8);
            font-size: 13.5px;
            pointer-events: none;
            user-select: none;
        }
        .rte-content {
            outline: none;
            min-height: inherit;
            font-size: 13.5px;
            line-height: 1.6;
            color: var(--bm-text, #0f172a);
        }
        .rte-content:focus { outline: none; }
        .rte-content h1 {
            font-size: 18px;
            font-weight: 700;
            margin: 18px 0 8px;
            color: var(--bm-primary, #0B3C5D);
            letter-spacing: -0.01em;
        }
        .rte-content h1:first-child { margin-top: 0; }
        .rte-content h2 {
            font-size: 14.5px;
            font-weight: 700;
            margin: 14px 0 4px;
            color: var(--bm-text, #0f172a);
        }
        .rte-content h3 {
            font-size: 13px;
            font-weight: 700;
            margin: 12px 0 4px;
            color: var(--bm-text, #0f172a);
        }
        .rte-content p { margin: 0 0 8px; }
        .rte-content ul, .rte-content ol {
            margin: 0 0 10px;
            padding-left: 22px;
        }
        .rte-content li { margin: 2px 0; }
        .rte-content blockquote {
            border-left: 3px solid var(--bm-accent, #3BC6B4);
            padding: 4px 12px;
            color: var(--bm-text-muted, #64748b);
            margin: 8px 0;
        }
        .rte-content strong { font-weight: 700; }
        .rte-content u { text-decoration: underline; }
        .rte-content em { font-style: italic; }
        .rte-content a {
            color: var(--bm-primary, #0B3C5D);
            text-decoration: underline;
        }
        .rte-content hr {
            border: none;
            border-top: 1px solid var(--bm-border, #e2e8f0);
            margin: 14px 0;
        }
        `}</style>
    );
}
